/**
 * One detected transfer candidate (Phase 3, §6.5). Shows both legs side by side
 * with the matched magnitude, the date gap, an IBAN-cross-match badge and the
 * confidence chip, plus the **Verknüpfen** / **Ignorieren** actions. Linking is
 * disabled offline (writes are never queued).
 */
import { ArrowRight, Link2, ShieldCheck, X } from 'lucide-react';
import { Button } from '../../components/ui/Button.tsx';
import { money } from '../../lib/format.ts';
import type { TransferCandidateOut } from '../../lib/types.ts';
import { ConfidenceChip } from './ConfidenceChip.tsx';
import { TransferLeg } from './TransferLeg.tsx';

export interface TransferCandidateCardProps {
  candidate: TransferCandidateOut;
  onLink: (candidate: TransferCandidateOut) => void;
  onIgnore: (candidate: TransferCandidateOut) => void;
  linking: boolean;
  online: boolean;
}

function dayLabel(days: number): string {
  if (days === 0) return 'gleicher Tag';
  return days === 1 ? '1 Tag Abstand' : `${days} Tage Abstand`;
}

export function TransferCandidateCard({
  candidate,
  onLink,
  onIgnore,
  linking,
  online,
}: TransferCandidateCardProps) {
  return (
    <div className="space-y-3 py-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-100">
          {money(candidate.amount)}
        </span>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {dayLabel(candidate.date_delta_days)}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {candidate.iban_cross_match ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-semibold text-sky-800 dark:bg-sky-950 dark:text-sky-300">
              <ShieldCheck className="h-3 w-3" aria-hidden />
              IBAN bestätigt
            </span>
          ) : null}
          <ConfidenceChip confidence={candidate.confidence} />
        </div>
      </div>

      <div className="flex items-stretch gap-2">
        <TransferLeg leg={candidate.out_txn} />
        <div className="flex shrink-0 items-center text-slate-400" aria-hidden>
          <ArrowRight className="h-5 w-5" />
        </div>
        <TransferLeg leg={candidate.in_txn} />
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onIgnore(candidate)}
          disabled={linking}
        >
          <X className="h-4 w-4" />
          Ignorieren
        </Button>
        <Button
          size="sm"
          onClick={() => onLink(candidate)}
          disabled={linking || !online}
          title={online ? undefined : 'Offline nicht möglich'}
        >
          <Link2 className="h-4 w-4" />
          {linking ? 'Verknüpfe …' : 'Verknüpfen'}
        </Button>
      </div>
    </div>
  );
}
